import React, { useState, useEffect } from "react";
import apiClient from "../../api/Api";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const AddCompany = () => {
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    companycode: "",
    companyname: "",
    companyaddress: "",
  });
  const [companylogo, setCompanyLogo] = useState(null);
  const [errors, setErrors] = useState({
    companycode: "",
    companyname: "",
    companyaddress: "",
  });
  const [editId, setEditId] = useState(null);

  const fetchCompanies = async () => {
    try {
      const res = await apiClient.get(
        `${process.env.REACT_APP_NEW_DATA}/getcompanydetails`
      );
      setCompanies(res.data.companyDetails || []);
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      setCompanies([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const { companycode, companyname, companyaddress } = formData;
    let newErrors = {};

    if (!companycode.trim())
      newErrors.companycode = "Company Code is required.";
    if (!companyname.trim())
      newErrors.companyname = "Company Name is required.";
    if (!companyaddress.trim())
      newErrors.companyaddress = "Company Address is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const data = new FormData();
    data.append("companycode", companycode.trim());
    data.append("companyname", companyname.trim());
    data.append("companyaddress", companyaddress.trim());
    if (companylogo) {
      data.append("companylogo", companylogo);
    }

    try {
      if (editId) {
        // For PUT with FormData, use method override via POST
        await apiClient.post(
          `${process.env.REACT_APP_NEW_DATA}/updatecompany/${editId}`,
          data,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        const res = await apiClient.post(
          `${process.env.REACT_APP_NEW_DATA}/addcompanydetails`,
          data,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (res.status === 201) {
          toast.error(res.data.message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
        }
      }

      setFormData({ companycode: "", companyname: "", companyaddress: "" });
      setCompanyLogo(null);
      setEditId(null);
      setShowForm(false);
      fetchCompanies();
    } catch (err) {
      console.error("Failed to save company:", err);
    }
  };

  const handleEdit = (company) => {
    setFormData({
      companycode: company.companycode,
      companyname: company.companyname,
      companyaddress: company.companyaddress,
    });
    setCompanyLogo(null); // clear previous logo selection
    setEditId(company.company_id);
    setShowForm(true);
  };

  const handleDelete = async (company_id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this company?"
    );
    if (!confirmDelete) return;

    try {
      await apiClient.delete(
        `${process.env.REACT_APP_NEW_DATA}/deletecompany/${company_id}`
      );
      fetchCompanies();
    } catch (err) {
      console.error("Failed to delete company:", err);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Company List</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setShowForm(!showForm);
            setFormData({
              companycode: "",
              companyname: "",
              companyaddress: "",
            });
            setCompanyLogo(null);
            setEditId(null);
          }}
        >
          {showForm ? "Close Form" : "Add Company"}
        </Button>
      </Box>

      {showForm && (
        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Company Code"
                name="companycode"
                fullWidth
                value={formData.companycode}
                onChange={handleInputChange}
                error={!!errors.companycode}
                helperText={errors.companycode}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Company Name"
                name="companyname"
                fullWidth
                value={formData.companyname}
                onChange={handleInputChange}
                error={!!errors.companyname}
                helperText={errors.companyname}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Company Address"
                name="companyaddress"
                fullWidth
                value={formData.companyaddress}
                onChange={handleInputChange}
                error={!!errors.companyaddress}
                helperText={errors.companyaddress}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCompanyLogo(e.target.files[0])}
                style={{ marginTop: "16px" }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSubmit}>
                {editId ? "Update" : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={2}>
        {companies.length === 0 ? (
          <Grid item xs={12}>
            <Typography>No companies found.</Typography>
          </Grid>
        ) : (
          companies.map((company) => (
            <Grid item xs={12} key={company.company_id}>
              <Card>
                <CardContent sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(company)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(company.company_id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="subtitle2">
                    Code: {company.companycode}
                  </Typography>
                  <Typography variant="h6">
                    Name: {company.companyname}
                  </Typography>
                  <Typography color="text.secondary">
                    Address: {company.companyaddress}
                  </Typography>
                  {company.companylogo && (
                    <Box mt={2}>
                      <img
                        src={company.companylogo}
                        alt="Company Logo"
                        style={{ width: 100, height: 100 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default AddCompany;
